import React, { useState, useEffect } from 'react';
import {
    ChevronDown, ChevronUp, Info, Clock, User, Globe, Target, ArrowRight, Check
} from 'lucide-react';
import { useSalesData } from '../context/SalesDataContext';
import AppendixWrapper from './common/AppendixWrapper';
import { useAppendix } from '../context/AppendixContext';

const FillButton = ({ field, value, label }) => {
    const { updateSectionData } = useSalesData();
    const [filled, setFilled] = useState(false);
    if (!value) return null;
    const handleFill = (e) => {
        e.stopPropagation();
        updateSectionData('SalesForm', { [field]: value }, 'zoho');
        setFilled(true);
        setTimeout(() => setFilled(false), 1500);
    };
    return (
        <button
            onClick={handleFill}
            title={`Enviar al formulario: ${label || field} `}
            className={`ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold transition-all duration-200
                ${filled
                    ? 'bg-emerald-500/30 text-emerald-400 border border-emerald-500/40'
                    : 'bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 hover:text-sky-300 border border-sky-500/40 hover:border-sky-500/60 shadow-lg shadow-sky-500/5'
                }`}
        >
            {filled ? <Check size={10} /> : <ArrowRight size={10} />}
            {filled ? 'OK' : '→'}
        </button>
    );
};

const LeadInfoCard = ({ contactId, onClose }) => {
    const [isOpen, setIsOpen] = useState(true);
    const { sectionData } = useSalesData();
    const leadData = sectionData?.LeadInfoCard || {};
    const { registerAppendix, setAppendixStatus } = useAppendix();
    const id = 'lead-info';

    useEffect(() => {
        registerAppendix(id, {
            title: 'Información del Lead',
            icon: Info
        });
    }, [registerAppendix]);

    useEffect(() => {
        setAppendixStatus(id, !!contactId);
    }, [contactId, setAppendixStatus]);

    if (!contactId) return null;

    return (
        <AppendixWrapper 
            id={id} 
            title="Información del Lead" 
            icon={Info} 
            className="w-[420px]"
            onClose={onClose}
            defaultPosition={{ x: 260, y: window.innerHeight - 500 }}
        >
            <div className="flex flex-col bg-slate-950">
                {/* Header Sub-info */}
                <div 
                    className="flex items-center justify-between p-4 bg-slate-900/50 border-b border-slate-800 cursor-pointer hover:bg-slate-800/50 transition-colors"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-500/20 text-blue-400">
                            <User size={18} />
                        </div>
                        <div>
                            <span className="font-semibold text-slate-100 block text-sm">Detalles de Captación</span>
                            {leadData.full_name && (
                                <span className="text-[10px] text-slate-400 uppercase font-bold tracking-tight flex items-center gap-1">
                                    {leadData.full_name}
                                    <FillButton field="full_name" value={leadData.full_name} label="Nombre" />
                                </span>
                            )}
                        </div>
                    </div>
                    {isOpen ? <ChevronDown size={20} className="text-slate-400" /> : <ChevronUp size={20} className="text-slate-400" />}
                </div>

                {/* Body */}
                {isOpen && (
                    <div className="p-5 space-y-4 bg-slate-900 border-t border-slate-800 flex-1 overflow-y-auto max-h-[400px]">
                        <div className="grid grid-cols-1 gap-4">
                            {/* Aglutinador */}
                            <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-800">
                                <div className="p-1.5 bg-purple-500/10 rounded-md">
                                    <Target size={14} className="text-purple-400" />
                                </div>
                                <div className="flex-1">
                                    <span className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Aglutinador</span>
                                    <span className="text-xs text-slate-200 font-medium">{leadData.aglutinator || 'No disponible'}</span>
                                </div>
                            </div>

                            {/* Origen IT */}
                            <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-800">
                                <div className="p-1.5 bg-emerald-500/10 rounded-md">
                                    <Globe size={14} className="text-emerald-400" />
                                </div>
                                <div className="flex-1">
                                    <span className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Origen IT</span>
                                    <span className="text-xs text-slate-200 font-medium">{leadData.origen_it || 'No disponible'}</span>
                                </div>
                            </div>

                            {/* Interés */}
                            <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-800">
                                <div className="p-1.5 bg-blue-500/10 rounded-md">
                                    <Info size={14} className="text-blue-400" />
                                </div>
                                <div className="flex-1">
                                    <span className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Interés</span>
                                    <span className="text-xs text-slate-200 font-medium">{leadData.interes_original || 'No disponible'}</span>
                                </div>
                            </div>

                            {/* Campañas Recientes */}
                            <div className="flex flex-col gap-2 p-3 bg-slate-800/30 rounded-lg border border-slate-800/50">
                                <span className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">Campañas Recientes</span>
                                <div className="flex flex-wrap gap-1.5">
                                    {leadData.campaigns && leadData.campaigns.length > 0 ? (
                                        leadData.campaigns.map((camp, idx) => (
                                            <span
                                                key={idx}
                                                className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-[10px] font-bold rounded border border-blue-500/20"
                                            >
                                                {camp}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-[10px] text-slate-500 italic">No hay historial de campañas</span>
                                    )}
                                </div>
                            </div>

                            {/* Fecha Creación */}
                            <div className="flex items-center gap-3 px-1">
                                <Clock size={12} className="text-slate-500" />
                                <span className="text-[10px] text-slate-500 font-medium">
                                    Lead creado el {leadData.created_time ? new Date(leadData.created_time).toLocaleString() : 'N/A'}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AppendixWrapper>
    );
};

export default LeadInfoCard;
